import React from 'react';
import { animated, useSpring, type SpringValues } from '@react-spring/web';
import type { ResponseCluster, ResponseText } from '@mentimeter/http-clients';
import { CheckIcon } from '@mentimeter/ragnar-visuals';
import { GroupTag } from '@mentimeter/slides-presentation-ui/groups/group-tag';
import { MasonryGrid } from '@mentimeter/slides-presentation-ui/masonry-grid';
import { ClickableHeading } from '@mentimeter/slides-presentation-ui/groups/clickable-heading';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useTranslations } from 'next-intl';
import { usePresentationContext } from '../../../../../legacy-context/presentationContext';
import { Response } from '../../Response';
import { ResponseWithUpvotes } from '../../ResponseWithUpvotes';
import { UNGROUPED_CLUSTER_ID } from '../../../groups';
import { getVotingOnAnswersSplitResults } from '../../utils/getVotingOnAnswersSplitResults';
import { useAssignedClusterStore } from '../utils/useAssignedClusterStore';
import { AnimatedGrid } from '../motion/AnimatedGrid';
import { OpenEndedTitleDataHandler } from '../../ui/OpenEndedTitleDataHandler';
import { EditableTitle } from './EditableTitle';
import { ContextMessage } from './ContextMessage';
import {
  GroupModerationMenu,
  type ModerationGroup,
} from './GroupModerationMenu';

const useCanEditTitle = (id: ResponseCluster['id']) => {
  const { isPreviewResultsEnabled, useConfig } = usePresentationContext();
  const { readonly } = useConfig();
  const isUngroupedGroup = id === UNGROUPED_CLUSTER_ID;

  return !isUngroupedGroup && !isPreviewResultsEnabled && !readonly;
};

export interface GroupDetailViewProps {
  onClose: () => void;
  title: string;
  id: ResponseCluster['id'];
  responses: ResponseText[];
  isTopVoted: boolean;
  hasUncategorizedTag?: boolean;
  totalVotes: number;
  shape: React.ReactNode;
  onTitleUpdate: (title: string) => void;
  navigation: React.ReactNode;
  moderationOptions: ModerationGroup[];
  onCreateNewGroup: (response: ResponseText) => void;
  onAssignToGroup: (targetGroupId: string, response: ResponseText) => void;
  shouldShowVotes?: boolean;
  numberOfColumns?: number;
}

export const GroupDetailView = ({
  onClose,
  title,
  id,
  responses,
  isTopVoted,
  hasUncategorizedTag = false,
  totalVotes,
  shape,
  onTitleUpdate,
  navigation,
  moderationOptions,
  onCreateNewGroup,
  onAssignToGroup,
  shouldShowVotes = true,
  numberOfColumns = 3,
}: GroupDetailViewProps) => {
  const { useConfig } = usePresentationContext();
  const translate = useTranslations('presenting.legacy');
  const { readonly } = useConfig();
  const canEditTitle = useCanEditTitle(id);

  const { resultsWithVotes, resultsWithoutVotes } =
    getVotingOnAnswersSplitResults(responses);

  const shouldShowWithoutVotesSectionHeading =
    resultsWithVotes.length > 0 &&
    resultsWithoutVotes.length > 0 &&
    shouldShowVotes;
  const votesLabel =
    totalVotes === 1 ? translate('message.vote') : translate('message.votes');

  const noVotesSectionResults = shouldShowVotes
    ? resultsWithoutVotes
    : responses;

  const {
    show: shouldShowEditNotification,
    targetClusterName: notificationGroupName,
    setShouldShow: setShouldShowEditNotification,
  } = useAssignedClusterStore((state) => state);

  const spring = useSpring({
    from: { top: '55px' },
    to: { top: '0px' },
    config: { tension: 200, friction: 20, duration: 200 },
  });

  const numberOfResponses = responses.length;
  const responseWord =
    numberOfResponses === 1
      ? translate('message.response')
      : translate('message.responses');

  return (
    <Box
      width="100%"
      height="100%"
      flex="0 0 auto"
      data-testid="open-ended-group-detail-view"
    >
      {readonly ? (
        <OpenEndedTitleDataHandler mb="space0" />
      ) : (
        <ClickableHeading onClick={onClose}>
          <OpenEndedTitleDataHandler mb="space0" />
        </ClickableHeading>
      )}
      <Box width="100%" height="100%" overflow="scroll" flex="1 1 auto">
        <Box
          as={animated.div}
          style={spring as SpringValues}
          height="100%"
          width="100%"
          bg="neutralWeakest"
          px="space10"
          pt="space10"
          pb="space16"
          borderRadius="40px"
          overflow="scroll"
          gap="space10"
        >
          <Box flexDirection="row" alignItems="center" width="100%">
            <Box width="64px" mr="space4">
              {shape}
            </Box>
            <Box justifyContent="space-between">
              <Box
                flexDirection="row"
                alignItems="center"
                gap="space4"
                mb="space2"
              >
                <EditableTitle
                  titleText={title}
                  onTextUpdate={onTitleUpdate}
                  isEditable={canEditTitle}
                  placeholder={translate('clustering.untitled_group')}
                />
                {hasUncategorizedTag && (
                  <GroupTag>{translate('open.uncategorized_label')}</GroupTag>
                )}
              </Box>
              {shouldShowVotes ? (
                <Text fontWeight="semiBold" fontSize={8} color="text">
                  {totalVotes}&nbsp;
                  <Text fontWeight="regular" fontSize={6}>
                    {votesLabel}
                  </Text>
                </Text>
              ) : (
                <Text lineHeight="body" fontSize={5} mb={2}>
                  {numberOfResponses} {responseWord}
                </Text>
              )}
            </Box>
            {isTopVoted && shouldShowVotes && (
              <Box
                alignSelf="flex-start"
                bg="primary"
                borderRadius={3}
                px={3}
                py={2}
                ml="20px"
              >
                <Text color="onPrimary" fontSize={3} fontWeight="semiBold">
                  {translate('message.top_voted')}
                </Text>
              </Box>
            )}
            <ContextMessage
              icon={<CheckIcon color="positive" />}
              text={`${translate(
                'clustering.response_moved',
              )} ${notificationGroupName}`}
              shouldShow={shouldShowEditNotification}
              resetShowState={() => setShouldShowEditNotification(false)}
            />
            {navigation}
          </Box>
          <Box as="section" width="100%" px="80px">
            {shouldShowVotes && (
              <AnimatedGrid numberOfColumns={numberOfColumns}>
                {resultsWithVotes.map((response, index) => (
                  <ResponseWithUpvotes
                    key={response.id}
                    index={index}
                    responseText={response.text}
                    participantDisplayName={null}
                    upvotes={response.upvotes}
                    isTopVoted={false}
                    onClick={() => {}}
                    isTextHidden={false}
                    isReadonly={readonly}
                    controls={
                      <GroupModerationMenu
                        response={response}
                        currentGroupId={id}
                        moderationOptions={moderationOptions}
                        onCreateNewGroup={onCreateNewGroup}
                        onAssignToGroup={onAssignToGroup}
                        showOnboardingEnabled={index === 0}
                      />
                    }
                  />
                ))}
              </AnimatedGrid>
            )}
          </Box>
          <Box as="section" width="100%" px="80px">
            {shouldShowWithoutVotesSectionHeading && (
              <Text fontWeight="regular" fontSize={5} mb="space12">
                {translate('open.responses_without_votes')}
              </Text>
            )}
            <MasonryGrid numberOfColumns={numberOfColumns} shouldAnimate>
              {noVotesSectionResults.map((response, index) => (
                <Response
                  shouldAnimate={true} // groups will always animate, since they are only show in the live mode
                  isReadonly={readonly}
                  isTextHidden={false}
                  key={response.id}
                  index={index}
                  responseText={response.text}
                  onClick={() => {}}
                  bg="neutralWeak"
                  controls={
                    <GroupModerationMenu
                      response={response}
                      currentGroupId={id}
                      moderationOptions={moderationOptions}
                      onCreateNewGroup={onCreateNewGroup}
                      onAssignToGroup={onAssignToGroup}
                      showOnboardingEnabled={
                        index === 0 && resultsWithVotes.length === 0
                      }
                    />
                  }
                />
              ))}
            </MasonryGrid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
